/**
 * Purchase orders raised against one sales order.
 *
 * Issuing one prints a PO for signature and queues the payable at the same
 * time, so the card says so plainly before the button is pressed — nobody
 * should discover later that finance already has it.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  ChevronDown,
  ChevronUp,
  Download,
  FileSignature,
  Plus,
  Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ApiError, api, getAccessToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso, shortDate } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';
import type { Quotation } from './QuotationsCard';
import {
  EMPTY_LINE,
  LineItemsEditor,
  filledLines,
  linesTotal,
  type DraftLine,
} from './LineItemsEditor';

export interface PurchaseOrder {
  id: string;
  code: string;
  poDate: string;
  description: string | null;
  amount: string;
  vatIncluded: boolean;
  amountVatEx: string;
  vatAmount: string;
  total: string;
  paymentDate: string | null;
  paymentRef: string | null;
  deliveryDate: string | null;
  status: 'ISSUED' | 'CANCELLED';
  cancelReason: string | null;
  supplier: { id: string; code: string; name: string };
  paymentMode: { id: string; code: string; label: string } | null;
  issuer: { name: string };
  salesOrder: { id: string; code: string; projectName: string; customer: { name: string } };
  project: { id: string; code: string; name: string } | null;
  quotation: { id: string; quoteRef: string | null; amount: string } | null;
  images: { id: string; sortOrder: number; file: { id: string; originalName: string } }[];
  apEntry: { id: string; code: string; status: string; paidDate: string | null } | null;
}

interface SupplierOption {
  id: string;
  name: string;
}

interface ParameterGroup {
  key: string;
  values: { id: string; code: string; label: string; active: boolean }[];
}

export function downloadPo(po: PurchaseOrder): void {
  void (async () => {
    const response = await fetch(`/api/v1/purchase-orders/${po.id}/pdf`, {
      credentials: 'include',
      headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${po.code}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  })();
}

const AP_TONE: Record<string, 'ok' | 'warning' | 'critical' | 'neutral'> = {
  COMPLETED: 'ok',
  PENDING: 'warning',
  DISAPPROVED: 'critical',
  CANCELLED: 'neutral',
};

export function PurchaseOrdersCard({
  salesOrderId,
  leadId,
}: {
  salesOrderId: string;
  /** Lets the form offer this job's quotations to order against. */
  leadId?: string;
}): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<PurchaseOrder | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([{ ...EMPTY_LINE }]);
  /** Screenshots of the supplier's items, printed in the order shown. */
  const [itemsImages, setItemsImages] = useState<{ fileId: string; preview: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [draft, setDraft] = useState({
    supplierId: '',
    quotationId: '',
    poDate: new Date().toISOString().slice(0, 10),
    description: '',
    amount: '',
    vatIncluded: 'true',
    paymentModeId: '',
    paymentDate: '',
    paymentRef: '',
    deliveryDate: '',
    terms: '',
    remarks: '',
  });

  const orders = useQuery({
    queryKey: ['purchase-orders', salesOrderId],
    queryFn: () =>
      api<{ data: PurchaseOrder[] }>(`/purchase-orders?salesOrderId=${salesOrderId}&pageSize=100`),
  });

  const suppliers = useQuery({
    queryKey: ['suppliers', 'options'],
    queryFn: () =>
      api<{ data: SupplierOption[] }>('/suppliers?status=ACTIVE&pageSize=200&sort=name'),
    enabled: creating,
  });

  const quotations = useQuery({
    queryKey: ['quotations', leadId],
    queryFn: () => api<{ data: Quotation[] }>(`/leads/${leadId}/quotations`),
    enabled: creating && Boolean(leadId),
  });

  const parameters = useQuery({
    queryKey: ['parameters'],
    queryFn: () => api<{ data: ParameterGroup[] }>('/parameters'),
    enabled: creating,
  });
  const paymentModes =
    parameters.data?.data.find((group) => group.key === 'payment_mode')?.values.filter((value) => value.active) ??
    [];

  /**
   * Picking a quotation copies the supplier's own lines onto the order, word
   * for word — that is what makes the PO checkable against their invoice.
   */
  const chooseQuotation = (quotationId: string): void => {
    const quotation = quotations.data?.data.find((row) => row.id === quotationId);
    if (quotation && quotation.items.length > 0) {
      setLines(
        quotation.items.map((item) => ({
          particular: item.particular,
          unit: item.unit ?? '',
          qty: String(Number(item.qty)),
          unitPrice: String(Number(item.unitPrice)),
        })),
      );
    } else if (quotation) {
      // No lines on the quotation: one row with its description and amount.
      setLines([
        {
          particular: quotation.description ?? 'As per supplier quotation',
          unit: '',
          qty: '1',
          unitPrice: String(Number(quotation.amount)),
        },
      ]);
    }
    setDraft((current) => ({
      ...current,
      quotationId,
      ...(quotation
        ? {
            supplierId: quotation.supplier.id,
            amount: quotation.amount,
            vatIncluded: quotation.vatIncluded ? 'true' : 'false',
            description: quotation.description ?? current.description,
          }
        : {}),
    }));
  };

  // A quotation from another supplier is not this order's any more.
  useEffect(() => {
    if (!draft.quotationId) return;
    const quotation = quotations.data?.data.find((row) => row.id === draft.quotationId);
    if (quotation && quotation.supplier.id !== draft.supplierId) {
      setDraft((current) => ({ ...current, quotationId: '' }));
    }
  }, [draft.supplierId, draft.quotationId, quotations.data]);

  const addImages = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const body = new FormData();
        body.append('file', file);
        const response = await fetch('/api/v1/purchase-orders/items-image', {
          method: 'POST',
          credentials: 'include',
          headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
          body,
        });
        const payload = (await response.json().catch(() => null)) as
          | { data?: { fileId: string }; message?: string }
          | null;
        if (!response.ok || !payload?.data) {
          throw new ApiError(response.status, payload?.message ?? 'Could not upload the image.');
        }
        const uploaded = { fileId: payload.data.fileId, preview: URL.createObjectURL(file) };
        setItemsImages((current) => [...current, uploaded]);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not upload the image.');
    } finally {
      setUploading(false);
    }
  };

  /** Order matters: the pages print exactly as they are listed here. */
  const moveImage = (index: number, by: -1 | 1): void => {
    setItemsImages((current) => {
      const next = [...current];
      const target = index + by;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const create = useMutation({
    mutationFn: () =>
      api<{ data: PurchaseOrder }>('/purchase-orders', {
        method: 'POST',
        body: {
          salesOrderId,
          supplierId: draft.supplierId,
          quotationId: draft.quotationId || undefined,
          poDate: draft.poDate,
          description: draft.description.trim(),
          items: filledLines(lines).length > 0 ? filledLines(lines) : undefined,
          amount: filledLines(lines).length > 0 ? undefined : draft.amount,
          vatIncluded: draft.vatIncluded === 'true',
          paymentModeId: draft.paymentModeId || undefined,
          paymentDate: draft.paymentDate || undefined,
          paymentRef: draft.paymentRef.trim() || undefined,
          deliveryDate: draft.deliveryDate || undefined,
          terms: draft.terms.trim() || undefined,
          remarks: draft.remarks.trim() || undefined,
          itemsImageFileIds: itemsImages.length > 0 ? itemsImages.map((i) => i.fileId) : undefined,
        },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['quotations'] });
      void queryClient.invalidateQueries({ queryKey: ['payables'] });
      setCreating(false);
      setError(null);
      setLines([{ ...EMPTY_LINE }]);
      setItemsImages([]);
      setNotice(
        `${result.data.code} issued. ${result.data.apEntry?.code ?? 'A payable'} is now waiting for finance.`,
      );
      // The point of issuing is the signed copy, so hand it over at once.
      downloadPo(result.data);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not issue the purchase order.'),
  });

  const cancel = useMutation({
    mutationFn: (po: PurchaseOrder) =>
      api(`/purchase-orders/${po.id}/cancel`, { method: 'POST', body: { reason: reason.trim() } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['payables'] });
      setCancelling(null);
      setReason('');
      setNotice('Purchase order cancelled, and its payable withdrawn.');
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not cancel the purchase order.'),
  });

  const rows = orders.data?.data ?? [];
  const selectedMode = paymentModes.find((mode) => mode.id === draft.paymentModeId);
  const isCheck = selectedMode?.code === 'CHECK';

  return (
    <Card
      title={`Purchase orders${rows.length ? ` (${rows.length})` : ''}`}
      description="Issuing a PO prints it for signature and queues the payable for finance, charged to this project."
    >
      {notice && (
        <div className="mb-3">
          <Alert tone="ok">{notice}</Alert>
        </div>
      )}
      {error && !creating && !cancelling && (
        <div className="mb-3">
          <Alert tone="critical">{error}</Alert>
        </div>
      )}

      {orders.isLoading && <Skeleton className="h-24 w-full" />}

      {!orders.isLoading && rows.length === 0 && (
        <EmptyState
          title="Nothing ordered yet"
          description="Raise a purchase order against a supplier quotation, or directly."
        />
      )}

      {rows.length > 0 && (
        <TableWrap>
          <thead>
            <tr>
              <Th>PO</Th>
              <Th>Supplier</Th>
              <Th>Payment</Th>
              <Th align="right">Total</Th>
              <Th>Payable</Th>
              <Th>{''}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((row) => (
              <tr key={row.id}>
                <Td>
                  <p className="font-mono text-xs font-semibold">{row.code}</p>
                  <p className="text-xs text-slate-500">{shortDate(row.poDate)}</p>
                  {row.description && <p className="text-xs">{row.description}</p>}
                  {row.status === 'CANCELLED' && (
                    <StatusChip tone="critical">Cancelled</StatusChip>
                  )}
                </Td>
                <Td>{row.supplier.name}</Td>
                <Td>
                  <p className="text-xs">{row.paymentMode?.label ?? 'To be advised'}</p>
                  {row.paymentDate && (
                    <p className="text-xs text-slate-500">{shortDate(row.paymentDate)}</p>
                  )}
                  {row.paymentRef && (
                    <p className="text-xs text-slate-500">{row.paymentRef}</p>
                  )}
                </Td>
                <Td align="right">
                  {peso(row.total)}
                  <span className="block text-xs text-slate-500">
                    {peso(row.amountVatEx)} + {peso(row.vatAmount)} VAT
                  </span>
                </Td>
                <Td>
                  {row.apEntry ? (
                    <Link
                      to={`/payables?q=${row.apEntry.code}`}
                      className="inline-flex flex-col items-start"
                    >
                      <StatusChip tone={AP_TONE[row.apEntry.status] ?? 'neutral'}>
                        {row.apEntry.code}
                      </StatusChip>
                      <span className="mt-0.5 text-xs text-slate-500">
                        {row.apEntry.status === 'COMPLETED'
                          ? `paid ${shortDate(row.apEntry.paidDate)}`
                          : row.apEntry.status.toLowerCase()}
                      </span>
                    </Link>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </Td>
                <Td>
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                      aria-label={`Download ${row.code}`}
                      title="Download for signature"
                      onClick={() => downloadPo(row)}
                    >
                      <Download aria-hidden="true" className="h-4 w-4" />
                    </button>
                    {can('po.cancel') && row.status === 'ISSUED' && (
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        aria-label={`Cancel ${row.code}`}
                        title="Cancel"
                        onClick={() => {
                          setReason('');
                          setCancelling(row);
                        }}
                      >
                        <Ban aria-hidden="true" className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      {can('po.create') && (
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" className="h-4 w-4" /> Issue purchase order
          </Button>
        </div>
      )}

      {creating && (
        <Modal
          title="Issue purchase order"
          description="This prints the PO and queues the payable in one step."
          onClose={() => {
            setCreating(false);
            setError(null);
          }}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              create.mutate();
            }}
          >
            {error && <Alert tone="critical">{error}</Alert>}

            {leadId && (quotations.data?.data.length ?? 0) > 0 && (
              <Field label="Against quotation" hint="Fills in the supplier and the amount">
                <select
                  className={inputClass}
                  value={draft.quotationId}
                  onChange={(event) => chooseQuotation(event.target.value)}
                >
                  <option value="">Not from a quotation</option>
                  {quotations.data?.data.map((quotation) => (
                    <option key={quotation.id} value={quotation.id}>
                      {quotation.supplier.name} — {peso(quotation.amount)}
                      {quotation.quoteRef ? ` (${quotation.quoteRef})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Supplier" required>
              <select
                className={inputClass}
                value={draft.supplierId}
                onChange={(event) => setDraft({ ...draft, supplierId: event.target.value })}
                required
              >
                <option value="">Choose a supplier…</option>
                {suppliers.data?.data.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Summary of the order" required hint="One line describing the whole PO">
              <input
                className={inputClass}
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                required
                minLength={3}
              />
            </Field>

            <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center dark:border-slate-600">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Screenshots of the supplier&rsquo;s items
              </p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-slate-500">
                Printed down the middle of the purchase order, in the order shown here. Crop them
                to the item rows &mdash; whatever is in the picture goes to the supplier.
              </p>

              {itemsImages.length > 0 && (
                <ul className="mt-3 space-y-3">
                  {itemsImages.map((picture, index) => (
                    <li key={picture.fileId} className="flex flex-col items-center gap-2">
                      <img
                        src={picture.preview}
                        alt={`Supplier items, page ${index + 1} of ${itemsImages.length}`}
                        className="mx-auto max-h-64 rounded-lg border border-slate-200 dark:border-slate-700"
                      />
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-500">
                          Page {index + 1} of {itemsImages.length}
                        </span>
                        <button
                          type="button"
                          className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"
                          aria-label={`Move page ${index + 1} earlier`}
                          disabled={index === 0}
                          onClick={() => moveImage(index, -1)}
                        >
                          <ChevronUp aria-hidden="true" className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"
                          aria-label={`Move page ${index + 1} later`}
                          disabled={index === itemsImages.length - 1}
                          onClick={() => moveImage(index, 1)}
                        >
                          <ChevronDown aria-hidden="true" className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                          aria-label={`Remove page ${index + 1}`}
                          onClick={() =>
                            setItemsImages((current) =>
                              current.filter((_, position) => position !== index),
                            )
                          }
                        >
                          <Trash2 aria-hidden="true" className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-3 flex flex-col items-center gap-1">
                <input
                  className={`${inputClass} mx-auto max-w-sm`}
                  type="file"
                  accept="image/png,image/jpeg"
                  multiple
                  disabled={uploading}
                  onChange={(event) => {
                    void addImages(event.target.files);
                    event.target.value = '';
                  }}
                  aria-label="Screenshots of the supplier's items"
                />
                <p className="text-xs text-slate-500">
                  {uploading
                    ? 'Uploading…'
                    : itemsImages.length > 0
                      ? 'PNG or JPEG — add another page if the quotation runs on'
                      : 'PNG or JPEG, up to 6 MB each. Add as many pages as the quotation has.'}
                </p>
              </div>
            </div>

            <details className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
              <summary className="cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-200">
                Or type the items out{' '}
                {itemsImages.length > 0 ? '(not needed — you attached screenshots)' : ''}
              </summary>
              <div className="mt-3">
                <LineItemsEditor
                  lines={lines}
                  onChange={setLines}
                  vatIncluded={draft.vatIncluded === 'true'}
                />
              </div>
            </details>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Order amount"
                hint={
                  filledLines(lines).length > 0
                    ? 'Added up from the lines above'
                    : 'The total from the supplier\u2019s quotation'
                }
                required={filledLines(lines).length === 0}
              >
                <input
                  className={inputClass}
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={
                    filledLines(lines).length > 0 ? String(linesTotal(lines)) : draft.amount
                  }
                  disabled={filledLines(lines).length > 0}
                  onChange={(event) => setDraft({ ...draft, amount: event.target.value })}
                />
              </Field>
              <Field label="That amount is">
                <select
                  className={inputClass}
                  value={draft.vatIncluded}
                  onChange={(event) => setDraft({ ...draft, vatIncluded: event.target.value })}
                >
                  <option value="true">VAT inclusive</option>
                  <option value="false">VAT exclusive</option>
                </select>
              </Field>
              <Field label="PO date" required>
                <input
                  className={inputClass}
                  type="date"
                  value={draft.poDate}
                  onChange={(event) => setDraft({ ...draft, poDate: event.target.value })}
                  required
                />
              </Field>
              <Field label="Estimated delivery">
                <input
                  className={inputClass}
                  type="date"
                  value={draft.deliveryDate}
                  onChange={(event) => setDraft({ ...draft, deliveryDate: event.target.value })}
                />
              </Field>
              <Field label="Pay by">
                <select
                  className={inputClass}
                  value={draft.paymentModeId}
                  onChange={(event) => setDraft({ ...draft, paymentModeId: event.target.value })}
                >
                  <option value="">To be advised</option>
                  {paymentModes.map((mode) => (
                    <option key={mode.id} value={mode.id}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={isCheck ? 'Check date' : 'Payment date'}
                hint={isCheck ? 'The date written on the check' : 'When the supplier is to be paid'}
              >
                <input
                  className={inputClass}
                  type="date"
                  value={draft.paymentDate}
                  onChange={(event) => setDraft({ ...draft, paymentDate: event.target.value })}
                />
              </Field>
            </div>

            <Field label={isCheck ? 'Check number' : 'Payment reference'}>
              <input
                className={inputClass}
                value={draft.paymentRef}
                onChange={(event) => setDraft({ ...draft, paymentRef: event.target.value })}
              />
            </Field>

            <Field label="Terms and conditions" hint="Printed at the foot of the PO">
              <textarea
                className={inputClass}
                rows={2}
                value={draft.terms}
                onChange={(event) => setDraft({ ...draft, terms: event.target.value })}
              />
            </Field>

            <Field label="Remarks" hint="Anything else the supplier needs stated on the PO">
              <textarea
                className={inputClass}
                rows={2}
                value={draft.remarks}
                onChange={(event) => setDraft({ ...draft, remarks: event.target.value })}
              />
            </Field>

            <Alert tone="info">
              On save this PO downloads for signature, and a payable for the full amount is queued
              for finance and charged to this project.
            </Alert>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setCreating(false);
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  create.isPending ||
                  !draft.supplierId ||
                  (filledLines(lines).length === 0 && !draft.amount) ||
                  draft.description.trim().length < 3
                }
              >
                <FileSignature aria-hidden="true" className="h-4 w-4" /> Issue and download
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {cancelling && (
        <Modal
          title={`Cancel ${cancelling.code}?`}
          description="The purchase order is kept for the record, and its payable is withdrawn."
          onClose={() => setCancelling(null)}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              cancel.mutate(cancelling);
            }}
          >
            {error && <Alert tone="critical">{error}</Alert>}
            <Field label="Why" required>
              <textarea
                className={inputClass}
                rows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                required
                minLength={5}
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setCancelling(null)}>
                Keep it
              </Button>
              <Button type="submit" variant="danger" disabled={reason.trim().length < 5}>
                Cancel purchase order
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Card>
  );
}
